import { Controller, useForm } from 'react-hook-form';
import { Button } from 'src/components/ui/button';
import React, { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  booleanScheme,
  emailScheme,
  stringScheme,
  urlZendeskArticlesScheme,
} from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { ZENDESK_HELP_CENTER_URL } from 'src/constants/urls';
import {
  useCreateKnowledgeBaseCollectorMutation,
  useGetKnowledgeBaseCollectorsQuery,
  useSetupKnowledgeBaseCollectorMutation,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { generateUniqueName, INTEGRATION_NAMES_MAP } from 'src/utils/fileUtils';
import * as yup from 'yup';
import { Switch } from 'src/components/ui/switch';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { FreshdeskHelpCenterIsPrivate } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateFreshdeskIntegrationPopup/FreshdeskIntegrationForm2/FreshdeskHelpCenterFields/FreshdeskHelpCenterIsPrivate';
import { FreshdeskHelpCenterExcludedUrls } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateFreshdeskIntegrationPopup/FreshdeskIntegrationForm2/FreshdeskHelpCenterFields/FreshdeskHelpCenterExcludedUrls';

export const FreshdeskHelpCenterForm = ({
  freshdeskIntegrationType,
  prevStep = () => {},
  closePopup = (..._args: any[]) => {},
}: {
  freshdeskIntegrationType?: any;
  prevStep?: () => void;
  closePopup?: (...args: any[]) => void;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const [
    createCollector,
    {
      data: createCollectorData,
      isLoading: isLoadingCreateCollector,
      isSuccess: isSuccessCreateCollector,
    },
  ] = useCreateKnowledgeBaseCollectorMutation() as any;

  const [
    setupCollector,
    { isLoading: isLoadingSetupCollector, isSuccess: isSuccessSetupCollector },
  ] = useSetupKnowledgeBaseCollectorMutation();

  const { data: collectorsResponse, isLoading: isLoadingCollectors } =
    useGetKnowledgeBaseCollectorsQuery(
      {
        id: id,
        include_data: false,
      },
      {
        skip: !id,
      },
    );

  let type = null;

  switch (freshdeskIntegrationType) {
    case 'Help Center':
      type = 'freshdesk_helpcenter';
      break;
    case 'Tickets':
      type = 'freshdesk_tickets';
      break;
  }

  useEffect(() => {
    if (isSuccessCreateCollector) {
      notification.success(`${createCollectorData.name} Added Successfully.`);

      if (createCollectorData.enabled) {
        const body = {
          type: createCollectorData.type,
          name: `[CoSupportAI] Article Auto-Sync (${createCollectorData.zendesk_subdomain})`,
        };

        setupCollector({ knowledgeBaseId: id, collectorId: createCollectorData.id, body });
      } else {
        close(true);
      }
    }
  }, [isSuccessCreateCollector]);

  useEffect(() => {
    if (isSuccessSetupCollector) {
      notification.success(`${createCollectorData.name} Setup Successfully.`);
      close(true);
    }
  }, [isSuccessSetupCollector]);

  useEffect(() => {
    if (isLoadingCreateCollector || isLoadingSetupCollector || isLoadingCollectors) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingCreateCollector, isLoadingSetupCollector, isLoadingCollectors]);

  const prev = () => {
    prevStep();
  };

  const close = (shouldCloseSourceModal = false) => {
    closePopup(shouldCloseSourceModal);
  };

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, isValid },
    clearErrors,
  } = useForm<any>({
    defaultValues: {
      domain: '',
      freshdesk_email: '',
      freshdesk_api_key: '',
      freshdesk_subdomain: '',
      brand_id: '',
      type: type,
      is_private: 'public',
      enabled: false,
      name: '',
      excluded_articles: [],
    },
    resolver: yupResolver(
      yup.lazy((values) => {
        const isRequired = values.is_private === 'private' || values.enabled;

        const baseSchema = {
          enabled: booleanScheme({ required: true }),
          is_private: stringScheme({ required: true }),
          domain: stringScheme({ required: true }).label('Freshdesk Help Center Domain'),
          freshdesk_subdomain: stringScheme({ required: isRequired }).label(
            'Freshdesk API Subdomain',
          ),
          freshdesk_email: emailScheme({ required: isRequired, allowEmpty: !isRequired }).label(
            'Freshdesk Email',
          ),
          freshdesk_api_key: stringScheme({ required: isRequired }).label('Freshdeks API Key'),
          brand_id: stringScheme({ required: isRequired }).label('Freshdesk Brand ID'),
          excluded_articles: yup.array(urlZendeskArticlesScheme({ required: true })),
        };

        return yup.object({
          ...baseSchema,
        });
      }),
    ),
  });

  const submit = (data) => {
    if (isValid) {
      const isPrivate = data.is_private === 'private';
      const name = `${INTEGRATION_NAMES_MAP[data.type]} (${data.freshdesk_subdomain || data.domain.split('.')[0]})`;

      data.collect_private_articles = isPrivate;
      delete data.is_private;

      if (!isPrivate && !data.enabled) {
        delete data.brand_id;
        delete data.freshdesk_api_key;
        delete data.freshdesk_email;
        delete data.freshdesk_subdomain;
      }

      const body = {
        ...data,
        run_immediately: true,
        name: generateUniqueName(name, collectorsResponse),
      };

      createCollector({ id, body });
    }
  };

  const isPrivate = watch('is_private') === 'private';
  const enabled = watch('enabled');

  return (
    <form
      onSubmit={handleSubmit(submit)}
      id="freshdesk-integration-form-2"
      className={'h-full flex flex-col justify-between'}
    >
      <div className="flex flex-col gap-2.5 max-h-[500px] overflow-auto">
        <div className={'flex flex-col gap-[5px]'}>
          <FreshdeskHelpCenterIsPrivate control={control} />
        </div>
        <div className={'flex flex-col gap-[5px]'}>
          <Controller
            control={control}
            name="enabled"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <span className="flex flex-col gap-4">
                <span className="flex flex-col items-start gap-[5px]">
                  <div className={'flex flex-row items-center gap-[5px]'}>
                    <span className={'text-ebony_opacity_50 font-jamjuree text-xs font-medium'}>
                      Real-time sync
                    </span>
                    <InputInfoToolTip
                      infoToolTip={{
                        side: 'right',
                        description: <>Sync new content and updates in real time</>,
                      }}
                    />
                  </div>
                  <Switch type="black" checked={value} onCheckedChange={onChange} />
                </span>
              </span>
            )}
          />
        </div>
        <div className={'flex flex-col gap-[5px]'}>
          <Controller
            control={control}
            name="domain"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'text'}
                label={'Freshdesk Help Center Domain'}
                placeholder={'brand.freshdesk.com'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                infoTooltip={{
                  side: 'right',
                  description: <>Link to your Freshdesk Help Center</>,
                }}
              />
            )}
          />
        </div>
        <div className={'flex flex-col gap-[5px] bg-light rounded-sm-5'}>
          <FreshdeskHelpCenterExcludedUrls control={control} clearErrors={clearErrors} />
        </div>
        {(isPrivate || enabled) && (
          <div className={'flex flex-col gap-[5px]'}>
            <Controller
              control={control}
              name="freshdesk_email"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type={'email'}
                  label={'Freshdesk email'}
                  placeholder={'email@freshdesk.com'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                  infoTooltip={{
                    side: 'right',
                    description: (
                      <>Email address of the Freshdesk user used to generate the API key</>
                    ),
                  }}
                />
              )}
            />
          </div>
        )}
        {(isPrivate || enabled) && (
          <div className={'flex flex-col gap-[5px]'}>
            <Controller
              control={control}
              name="freshdesk_subdomain"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type={'text'}
                  label={'Freshdesk API Subdomain'}
                  placeholder={'brand'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                  infoTooltip={{
                    side: 'right',
                    description: (
                      <>
                        <a
                          href={ZENDESK_HELP_CENTER_URL.API_SUBDOMAIN}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline cursor-pointer"
                        >
                          Here
                        </a>{' '}
                        is where to find it
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>
        )}
        {(isPrivate || enabled) && (
          <div className={'flex flex-col gap-[5px]'}>
            <Controller
              control={control}
              name="freshdesk_api_key"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type={'password'}
                  label={'Freshdesk API Key'}
                  placeholder={'------------------------------------------------------'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                  infoTooltip={{
                    side: 'right',
                    description: (
                      <>
                        Check how to generate the API Key{' '}
                        <a
                          href={ZENDESK_HELP_CENTER_URL.API_KEY}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline cursor-pointer"
                        >
                          here →
                        </a>
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>
        )}
        {(isPrivate || enabled) && (
          <div className={'flex flex-col gap-[5px]'}>
            <Controller
              control={control}
              name="brand_id"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type={'text'}
                  label={'Freshdesk Brand ID'}
                  placeholder={'-------------------'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                  infoTooltip={{
                    side: 'right',
                    description: (
                      <>
                        Check how to find your Brand ID{' '}
                        <a
                          href={ZENDESK_HELP_CENTER_URL.BRAND_ID}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline cursor-pointer"
                        >
                          here →
                        </a>
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>
        )}
      </div>
      <div className="flex justify-center mt-5">
        <div className="flex gap-2.5">
          <Button variant="outline" type="button" className="min-w-24" onClick={() => close()}>
            Cancel
          </Button>
          <Button variant="outline-light" type="button" className="min-w-24" onClick={prev}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="9"
              height="10"
              viewBox="0 0 9 10"
              fill="none"
              className="mr-1.5"
            >
              <path
                d="M9 4.4375L2.15438 4.4375L5.29875 1.29312L4.5 0.499999L0 5L4.5 9.5L5.29313 8.70687L2.15438 5.5625L9 5.5625V4.4375Z"
                fill="#05050D"
              />
            </svg>
            <span>Back</span>
          </Button>
          <Button type="submit" className="min-w-24">
            <span>Integrate</span>
          </Button>
        </div>
      </div>
    </form>
  );
};
